import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { useGetUsers } from "../../../../backend/users";
import PageLoader from "../../../shared/PageLoader";
import { toast } from "react-toastify";
import "./EditDetails.css";

interface DropdownOption {
  id: string | number;
  name: string;
}

interface EditDetailsProps {
  isOpen: boolean;
  onClose: () => void;
  activeSection?: string;
  selectCity?: DropdownOption[];
  selectState?: DropdownOption[];
  initialData?: any;
  onSave?: (data: any, section: string) => void;
  isLoading?: boolean;
  onStateChange?: (stateId: string) => void;
}

const EditDetails: React.FC<EditDetailsProps> = ({
  isOpen,
  onClose,
  selectState = [],
  selectCity = [],
  activeSection = "license-dashboard",
  initialData = {},
  onSave,
  isLoading = false,
  onStateChange,
}) => {
  const [formData, setFormData] = useState<any>({
    license_name: "",
    description: "",
    assigned_to_user_id: "",
    watcher_ids: [],
    company_id: "",
    associate_company_id: "",
    location_id: "",
    name: "",
    is_perpetual: false,
    default_validity_days: "",
    renewal_days: "",
    scope: "",
    state_id: "",
    city_id: "",
    authority: "",
    reminder_days: [],
  });
  const [selectedScope, setSelectedScope] = useState<string>("");
  const [reminderCount, setReminderCount] = useState<number>(0);
  const [validationError, setValidationError] = useState<string>("");
  const prevIsOpenRef = useRef<boolean>(false);
  const [initialFormData, setInitialFormData] = useState<any>({});
  const [hasChanges, setHasChanges] = useState<boolean>(false);
  const [existingSOPs, setExistingSOPs] = useState<any[]>([]);
  const [initialExistingSOPs, setInitialExistingSOPs] = useState<any[]>([]);
  const [newSOPs, setNewSOPs] = useState<any>({
    new: { checked: false, sop: "" },
    renewal: { checked: false, sop: "" },
    amendment: { checked: false, sop: "" },
    surrender: { checked: false, sop: "" },
  });

  // Reset form function
  const resetForm = () => {
    setFormData({
      license_name: "",
      description: "",
      assigned_to_user_id: "",
      watcher_ids: [],
      company_id: "",
      associate_company_id: "",
      location_id: "",
      name: "",
      is_perpetual: false,
      default_validity_days: "",
      renewal_days: "",
      scope: "",
      state_id: "",
      city_id: "",
      authority: "",
      reminder_days: [],
    });
    setInitialFormData({});
    setSelectedScope("");
    setReminderCount(0);
    setValidationError("");
    setHasChanges(false);
    setExistingSOPs([]);
    setInitialExistingSOPs([]);
    setNewSOPs({
      new: { checked: false, sop: "" },
      renewal: { checked: false, sop: "" },
      amendment: { checked: false, sop: "" },
      surrender: { checked: false, sop: "" },
    });
  };

  // Handle close with reset
  const handleClose = () => {
    resetForm();
    onClose();
  };

  const { records: licenseOwners } = useGetUsers(
    {
      filters: {
        companies_id_in: [formData.associate_company_id],
        company_location_mappings_location_id_in: [formData.location_id],
        roles_name_in: ['License Owner']
      }
    },
    Boolean(formData.associate_company_id && formData.location_id)
  );

  const { records: licenseWatchers } = useGetUsers(
    {
      filters: {
        companies_id_in: [formData.associate_company_id],
        company_location_mappings_location_id_in: [formData.location_id],
        roles_name_in: ['License Watcher']
      }
    },
    Boolean(formData.associate_company_id && formData.location_id)
  );

  useEffect(() => {
    const isOpening = isOpen && !prevIsOpenRef.current;

    if (isOpening && initialData && Object.keys(initialData).length > 0) {
      const formDataSnapshot = {
        license_name: initialData.license_name || "",
        description: initialData.description || "",
        assigned_to_user_id: initialData.assigned_to_user_id || "",
        watcher_ids: initialData.watcher_ids || [],
        company_id: initialData.company_id || "",
        associate_company_id: initialData.associate_company_id || "",
        location_id: initialData.location_id || "",
        name: initialData.name || "",
        is_perpetual: initialData.is_perpetual || false,
        default_validity_days: initialData.default_validity_days || "",
        renewal_days: initialData.renewal_days || "",
        scope: initialData.scope || "",
        state_id: initialData.state_id || "",
        city_id: initialData.city_id || "",
        authority: initialData.authority || "",
        reminder_days: initialData.reminder_days || [],
      };

      setFormData(formDataSnapshot);

      const initialSnapshot = JSON.parse(JSON.stringify(formDataSnapshot));
      initialSnapshot.scope = initialData.scope || "";
      initialSnapshot.renewal_reminder_count = initialData.renewal_reminder_count || 0;
      setInitialFormData(initialSnapshot);

      if (initialData.scope) {
        setSelectedScope(initialData.scope);
      }

      setReminderCount(initialData.renewal_reminder_count || 0);

      if (initialData.existing_sops && Array.isArray(initialData.existing_sops)) {
        setExistingSOPs(initialData.existing_sops);
        setInitialExistingSOPs(JSON.parse(JSON.stringify(initialData.existing_sops)));
      }
    }

    // Update the ref to track current isOpen state
    prevIsOpenRef.current = isOpen;
  }, [initialData, isOpen]);

  // Update reminder_days array when reminderCount changes
  useEffect(() => {
    if (reminderCount === 0) {
      setFormData((prev: any) => ({
        ...prev,
        reminder_days: [],
      }));
    } else if (formData.reminder_days.length !== reminderCount) {
      const newReminderDays = [...formData.reminder_days];
      if (newReminderDays.length < reminderCount) {
        while (newReminderDays.length < reminderCount) {
          newReminderDays.push(0);
        }
      } else {
        newReminderDays.length = reminderCount;
      }
      setFormData((prev: any) => ({
        ...prev,
        reminder_days: newReminderDays,
      }));
    }
  }, [reminderCount]);

  useEffect(() => {
    if (Object.keys(initialFormData).length === 0) {
      setHasChanges(false);
      return;
    }

    if (activeSection === "license-dashboard") {
      const trimmedLicenseName = formData.license_name ? formData.license_name.trim() : formData.license_name;
      const trimmedDescription = formData.description ? formData.description.trim() : formData.description;

      const nameChanged = trimmedLicenseName !== initialFormData.license_name;
      const descChanged = trimmedDescription !== initialFormData.description;
      const assignedChanged = formData.assigned_to_user_id !== initialFormData.assigned_to_user_id;

      const currentWatchers = [...formData.watcher_ids].sort();
      const initialWatchers = [...initialFormData.watcher_ids].sort();
      const watchersChanged = JSON.stringify(currentWatchers) !== JSON.stringify(initialWatchers);

      setHasChanges(nameChanged || descChanged || assignedChanged || watchersChanged);
    } else if (activeSection === "license-application") {

      const assignedChanged = formData.assigned_to_user_id !== initialFormData.assigned_to_user_id;

      const currentWatchers = [...formData.watcher_ids].sort();
      const initialWatchers = [...initialFormData.watcher_ids].sort();
      const watchersChanged = JSON.stringify(currentWatchers) !== JSON.stringify(initialWatchers);

      setHasChanges(assignedChanged || watchersChanged);
    } else if (activeSection === "license-type") {
      const trimmedName = formData.name ? formData.name.trim() : formData.name;
      const trimmedAuthority = formData.authority ? formData.authority.trim() : formData.authority;

      const nameChanged = trimmedName !== initialFormData.name;
      const perpetualChanged = formData.is_perpetual !== initialFormData.is_perpetual;
      const validityChanged = formData.default_validity_days !== initialFormData.default_validity_days;
      const renewalChanged = formData.renewal_days !== initialFormData.renewal_days;
      const scopeChanged = selectedScope !== initialFormData.scope;
      const stateChanged = formData.state_id !== initialFormData.state_id;
      const cityChanged = formData.city_id !== initialFormData.city_id;
      const authorityChanged = trimmedAuthority !== initialFormData.authority;
      const reminderCountChanged = reminderCount !== initialFormData.renewal_reminder_count;

      const currentReminderDays = [...formData.reminder_days].sort();
      const initialReminderDays = [...(initialFormData.reminder_days || [])].sort();
      const reminderDaysChanged = JSON.stringify(currentReminderDays) !== JSON.stringify(initialReminderDays);

      // Check if SOPs have changed
      const initialSOPsData = initialExistingSOPs.reduce((acc: any, sop: any) => {
        acc[sop.action_type] = sop.sop || "";
        return acc;
      }, {});

      const currentSOPsData: any = {};

      // Add existing SOPs
      existingSOPs.forEach((sop: any) => {
        currentSOPsData[sop.action_type] = sop.sop || "";
      });

      // Add new SOPs
      Object.entries(newSOPs).forEach(([key, value]: [string, any]) => {
        if (value.checked && value.sop) {
          currentSOPsData[key] = value.sop;
        }
      });

      const sopsChanged = JSON.stringify(initialSOPsData) !== JSON.stringify(currentSOPsData);

      setHasChanges(
        nameChanged || perpetualChanged || validityChanged || renewalChanged ||
        scopeChanged || stateChanged || cityChanged || authorityChanged ||
        reminderCountChanged || reminderDaysChanged || sopsChanged
      );
    }
  }, [formData, initialFormData, activeSection, selectedScope, reminderCount, existingSOPs, newSOPs]);

  const handleInputChange = (field: string, value: any) => {
    const trimmedValue = typeof value === 'string' ? value.trimStart() : value;

    setFormData((prev: any) => ({
      ...prev,
      [field]: trimmedValue,
    }));
  };

  const validateRenewalDays = (renewalValue: number, validityValue: number) => {
    if (renewalValue >= validityValue) {
      setValidationError(
        "Up for Renewal days should be less than Default Validity Days."
      );
    } else {
      setValidationError("");
    }
  };

  const handleSubmit = () => {
    if (onSave) {
      let dataToSave: any = {};

      if (activeSection === "license-dashboard") {
        if (formData.license_name && formData.license_name.trim() === '') {
          toast.error("License Name cannot be empty");
          return;
        }

        if (!formData.assigned_to_user_id || formData.assigned_to_user_id.trim() === '') {
          toast.error("Assigned to is required");
          return;
        }

        if (!formData.watcher_ids || formData.watcher_ids.length === 0) {
          toast.error("At least one watcher must be selected");
          return;
        }

        const changedFields: any = {};

        const trimmedLicenseName = formData.license_name ? formData.license_name.trim() : formData.license_name;
        const trimmedDescription = formData.description ? formData.description.trim() : formData.description;

        if (trimmedLicenseName !== initialFormData.license_name) {
          changedFields.license_name = trimmedLicenseName;
        }
        if (trimmedDescription !== initialFormData.description) {
          changedFields.description = trimmedDescription;
        }
        if (formData.assigned_to_user_id !== initialFormData.assigned_to_user_id) {
          changedFields.assigned_to_user_id = formData.assigned_to_user_id;
        }

        const watcherIdsChanged = JSON.stringify(formData.watcher_ids.sort()) !== JSON.stringify(initialFormData.watcher_ids.sort());
        if (watcherIdsChanged) {
          changedFields.watcher_ids = formData.watcher_ids;
        }

        dataToSave = changedFields;
      } else if (activeSection === "license-application") {
        if (!formData.assigned_to_user_id || formData.assigned_to_user_id.trim() === '') {
          toast.error("Assigned to is required");
          return;
        }
        if (!formData.watcher_ids || formData.watcher_ids.length === 0) {
          toast.error("At least one watcher must be selected");
          return;
        }

        const changedFields: any = {};

        if (formData.assigned_to_user_id !== initialFormData.assigned_to_user_id) {
          changedFields.assigned_to_id = formData.assigned_to_user_id;
        }

        const currentWatchers = [...formData.watcher_ids].sort();
        const initialWatchers = [...initialFormData.watcher_ids].sort();
        const watcherIdsChanged = JSON.stringify(currentWatchers) !== JSON.stringify(initialWatchers);
        if (watcherIdsChanged) {
          changedFields.watcher_ids = formData.watcher_ids;
        }

        dataToSave = changedFields;
      } else if (activeSection === "license-type") {
        if (!formData.name || formData.name.trim() === '') {
          toast.error("License Type Name cannot be empty");
          return;
        }
        if (!formData.is_perpetual) {
          if (!formData.default_validity_days || formData.default_validity_days === '') {
            toast.error("Default Validity (in Days) is required");
            return;
          }

          if (!formData.renewal_days || formData.renewal_days === '') {
            toast.error("Up for Renewal (in Days) is required");
            return;
          }

          if (!reminderCount || reminderCount === 0) {
            toast.error("Number of Renewal Reminders is required");
            return;
          }

          if (
            formData.default_validity_days !== "" &&
            formData.renewal_days !== "" &&
            Number(formData.renewal_days) >= Number(formData.default_validity_days)
          ) {
            toast.error("Up for Renewal Days must be less than Default Validity Days.");
            return;
          }

          if (reminderCount > 0 && formData.reminder_days.length > 0) {
            const filledDays = formData.reminder_days.filter(
              (day: any) => day !== undefined && day !== null && day !== 0 && day !== ""
            );

            if (filledDays.length < reminderCount) {
              toast.error("Please fill all renewal reminder days.");
              return;
            }

            const uniqueDays = new Set(filledDays);
            if (uniqueDays.size !== filledDays.length) {
              toast.error("Renewal reminder days must be unique. Please remove duplicate values.");
              return;
            }
          }
        }

        if (selectedScope === "location") {
          if (!formData.state_id || formData.state_id.trim() === '') {
            toast.error("State is required when scope is Location");
            return;
          }
        }

        if (!formData.authority || formData.authority.trim() === '') {
          toast.error("Authority is required");
          return;
        }

        // Only include changed fields
        const changedFields: any = {};

        const trimmedName = formData.name ? formData.name.trim() : formData.name;
        const trimmedAuthority = formData.authority ? formData.authority.trim() : formData.authority;

        if (trimmedName !== initialFormData.name) {
          changedFields.name = trimmedName;
        }
        if (formData.is_perpetual !== initialFormData.is_perpetual) {
          changedFields.is_perpetual = formData.is_perpetual;
        }
        if (formData.default_validity_days !== initialFormData.default_validity_days) {
          changedFields.default_validity_days = formData.default_validity_days;
        }
        if (formData.renewal_days !== initialFormData.renewal_days) {
          changedFields.up_for_renewal_days = formData.renewal_days;
        }
        if (selectedScope !== initialFormData.scope) {
          changedFields.scope = selectedScope;
        }
        if (formData.state_id !== initialFormData.state_id) {
          changedFields.state_id = formData.state_id;
        }
        if (formData.city_id !== initialFormData.city_id) {
          changedFields.city_id = formData.city_id;
        }
        if (trimmedAuthority !== initialFormData.authority) {
          changedFields.authority = trimmedAuthority;
        }
        if (reminderCount !== initialFormData.renewal_reminder_count) {
          changedFields.renewal_reminder_count = reminderCount;
        }

        const currentReminderDays = [...formData.reminder_days].sort();
        const initialReminderDays = [...(initialFormData.reminder_days || [])].sort();
        const reminderDaysChanged = JSON.stringify(currentReminderDays) !== JSON.stringify(initialReminderDays);
        if (reminderDaysChanged) {
          changedFields.reminder_days = formData.reminder_days;
        }

        const sopDataArray: Array<{ id?: string; action_type: string; sop: string }> = [];

        if (existingSOPs && existingSOPs.length > 0) {
          existingSOPs.forEach((sopItem: any) => {
            if (sopItem.sop && sopItem.sop.trim() !== "") {
              sopDataArray.push({
                id: sopItem.id,
                action_type: sopItem.action_type,
                sop: sopItem.sop.trim(),
              });
            }
          });
        }

        Object.entries(newSOPs).forEach(([key, value]: [string, any]) => {
          if (value.checked && value.sop && value.sop.trim() !== "") {
            sopDataArray.push({
              action_type: key,
              sop: value.sop.trim(),
            });
          }
        });

        if (sopDataArray.length > 0) {
          changedFields.sop = sopDataArray;
        }

        dataToSave = changedFields;
      }

      onSave(dataToSave, activeSection);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {ReactDOM.createPortal(
      <div className="edit-screen-wrapper">
        <div className="license-popup-overlay">
          <div className="license-popup">
            <div className="popup-header d-flex justify-content-between align-items-center">
              <div>
                <h6 className="fw-semibold fs-5 m-0">
                  {activeSection === "license-application"
                    ? "Edit License Application Details"
                    : activeSection === "license-type"
                    ? "Edit License Type Details"
                    : "Edit License Details"}
                </h6>
              </div>
              <button
                className="btn-close mt-0"
                aria-label="Close"
                onClick={handleClose}
              ></button>
            </div>
            <hr className="mt-0"/>
            {activeSection === "license-dashboard" && (
              <div className="popup-body" id="license-dashboard">
                <div className="edit-container">
                  <label className="mb-1 text-md">License Name</label>
                  <input
                    type="text"
                    name="license_name"
                    id="license_name"
                    placeholder="License Name"
                    className="form-control"
                    value={formData.license_name}
                    onChange={(e) =>
                      handleInputChange("license_name", e.target.value)
                    }
                  />
                </div>
                <div className="edit-container">
                  <label className="mb-1 text-md">Description</label>
                  <textarea
                    name="description"
                    id="description"
                    placeholder="Add description"
                    className="form-control"
                    value={formData.description}
                    onChange={(e) =>
                      handleInputChange("description", e.target.value)
                    }
                  ></textarea>
                </div>
                <div className="edit-container">
                  <label className="mb-1 text-md">Assigned to<span>*</span></label>
                  <select
                    className="form-select"
                    value={formData.assigned_to_user_id}
                    onChange={(e) =>
                      handleInputChange("assigned_to_user_id", e.target.value)
                    }
                  >
                    <option value="">
                      Select an owner who will manage this license end to end
                    </option>
                    {(licenseOwners || []).map((user: any) => (
                      <option key={user.id} value={user.id}>
                        {user.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="edit-container">
                  <label className="mb-1 text-md">Watchers<span>*</span></label>
                  <div
                    className="watchers-box p-3"
                    style={{ maxHeight: "200px", overflowY: "auto" }}
                  >
                    {(licenseWatchers || []).map((watcher: any) => (
                      <div
                        key={watcher.id}
                        className="mb-3 d-flex gap-2 align-items-start"
                      >
                        <input
                          className="form-check"
                          type="checkbox"
                          id={`watcher-${watcher.id}`}
                          checked={formData.watcher_ids.includes(watcher.id)}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setFormData((prev: any) => ({
                              ...prev,
                              watcher_ids: checked
                                ? [...prev.watcher_ids, watcher.id]
                                : prev.watcher_ids.filter(
                                    (id: string) => id !== watcher.id
                                  ),
                            }));
                          }}
                        />
                        <label
                          className="form-check-label"
                          htmlFor={`watcher-${watcher.id}`}
                        >
                          <div className="fs-6">{watcher.name}</div>
                          <small className="text-sm">{watcher.email}</small>
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
            {activeSection === "license-application" && (
              <div className="popup-body" id="license-application">
                <div className="edit-container">
                  <label className="mb-1 text-md">Assigned to<span>*</span></label>
                  <select
                    className="form-select"
                    value={formData.assigned_to_user_id}
                    onChange={(e) =>
                      handleInputChange("assigned_to_user_id", e.target.value)
                    }
                  >
                    <option value="">
                      Select an owner who will manage this license end to end
                    </option>
                    {(licenseOwners || []).map((user: any) => (
                      <option key={user.id} value={user.id}>
                        {user.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="edit-container">
                  <label className="mb-1 text-md">Watchers<span>*</span></label>
                  <div
                    className="watchers-box p-3"
                    style={{ maxHeight: "200px", overflowY: "auto" }}
                  >
                    {(licenseWatchers || []).map((watcher: any) => (
                      <div
                        key={watcher.id}
                        className="mb-3 d-flex gap-2 align-items-start"
                      >
                        <input
                          className="form-check"
                          type="checkbox"
                          id={`watcher-${watcher.id}`}
                          checked={formData.watcher_ids.includes(watcher.id)}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setFormData((prev: any) => ({
                              ...prev,
                              watcher_ids: checked
                                ? [...prev.watcher_ids, watcher.id]
                                : prev.watcher_ids.filter(
                                    (id: string) => id !== watcher.id
                                  ),
                            }));
                          }}
                        />
                        <label
                          className="form-check-label"
                          htmlFor={`watcher-${watcher.id}`}
                        >
                          <div className="fs-6">{watcher.name}</div>
                          <small className="text-sm">{watcher.email}</small>
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
            {activeSection === "license-type" && (
              <div className="popup-body" id="license-type">
                <div className="edit-container">
                  <label className="mb-1 text-md">Name</label>
                  <input
                    type="text"
                    name="name"
                    id="name"
                    placeholder="Name"
                    className="form-control"
                    value={formData.name}
                    onChange={(e) => handleInputChange("name", e.target.value)}
                  />
                </div>
                {!formData.is_perpetual && (
                  <div className="edit-container">
                    <label className="mb-1 text-md">
                      Default Validity (in Days)<span>*</span>
                    </label>
                    <input
                      className="form-control"
                      type="number"
                      placeholder="Add in number of days"
                      value={formData.default_validity_days}
                      onChange={(e) => {
                        const value = Number(e.target.value);
                        handleInputChange("default_validity_days", value);
                        validateRenewalDays(
                          Number(formData.renewal_days),
                          value
                        );
                      }}
                      onWheel={(e) => e.currentTarget.blur()}
                    />
                  </div>
                )}
                <div className="edit-container">
                  <label className="mb-1 text-md">Scope</label>
                  <div className="d-flex gap-2 mb-2">
                    <input
                      type="radio"
                      id="central"
                      name="scope"
                      value="central"
                      checked={selectedScope === "central"}
                      onChange={() => {
                        setSelectedScope("central");
                        handleInputChange("scope", "central");
                      }}
                    />
                    <label htmlFor="central">Central</label>
                  </div>

                  <div className="d-flex gap-2">
                    <input
                      type="radio"
                      id="location"
                      name="scope"
                      value="location"
                      checked={selectedScope === "location"}
                      onChange={() => {
                        setSelectedScope("location");
                        handleInputChange("scope", "location");
                      }}
                    />
                    <label htmlFor="location">Location</label>
                  </div>

                  {selectedScope === "location" && (
                    <div className="ps-4 mb-3 mt-3">
                      <div className="mb-2">
                        <label className="mb-1 text-md">State<span>*</span></label>
                        <select
                          className="form-select"
                          value={formData.state_id}
                          onChange={(e) => {
                            const newStateId = e.target.value;
                            handleInputChange("state_id", newStateId);
                            handleInputChange("city_id", "");
                            if (onStateChange) {
                              onStateChange(newStateId);
                            }
                          }}
                        >
                          <option value="" disabled>
                            Select state
                          </option>
                          {selectState.map((state, index) => (
                            <option key={index} value={state.id}>
                              {state.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="mb-1 text-md">City</label>
                        <select
                          className="form-select"
                          value={formData.city_id}
                          onChange={(e) =>
                            handleInputChange("city_id", e.target.value)
                          }
                        >
                          <option value="" disabled>
                            Select city
                          </option>
                          {selectCity.map((city, index) => (
                            <option key={index} value={city.id}>
                              {city.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  )}
                </div>
                <div className="edit-container">
                  <label className="mb-1 text-md">Authority<span>*</span></label>
                  <input
                    className="form-control"
                    type="text"
                    placeholder="Add Authority Name"
                    value={formData.authority}
                    onChange={(e) =>
                      handleInputChange("authority", e.target.value)
                    }
                  />
                </div>

                <div className="edit-container">
                  <label className="mb-1 text-md">
                    Standard Operating Procedure<span>*</span>
                  </label>

                  {[
                    { key: "new", label: "New" },
                    { key: "renewal", label: "Renewal" },
                    { key: "amendment", label: "Amendment" },
                    { key: "surrender", label: "Surrender" },
                  ]
                    .filter(({ key }) => !(key === "renewal" && formData.is_perpetual))
                    .map(({ key, label }) => {
                      const existingSOP = existingSOPs.find((sop: any) => sop.action_type === key);
                      const isExisting = existingSOP !== undefined;
                      const isChecked = isExisting || newSOPs[key as keyof typeof newSOPs].checked;
                      const sopValue = isExisting ? existingSOP.sop : newSOPs[key as keyof typeof newSOPs].sop;

                      return (
                        <div key={key} className="d-flex flex-column gap-2">
                          <div className="d-flex align-items-center gap-2 mb-2">
                            <input
                              className="form-check"
                              type="checkbox"
                              id={key}
                              checked={isChecked}
                              disabled={isExisting}
                              onChange={() => {
                                if (!isExisting) {
                                  setNewSOPs((prev: any) => ({
                                    ...prev,
                                    [key]: {
                                      ...prev[key],
                                      checked: !prev[key].checked,
                                    },
                                  }));
                                }
                              }}
                            />
                            <label htmlFor={key} className="form-check-label">
                              <div className="fs-6">{label}</div>
                            </label>
                          </div>

                          {isChecked && (
                            <textarea
                              name={`sop_${key}`}
                              className="mb-2 form-control"
                              rows={2}
                              placeholder={`Add standard operating procedure for ${label}`}
                              value={sopValue || ""}
                              onChange={(e) => {
                                if (isExisting) {
                                  const updatedSOPs = existingSOPs.map((item: any) =>
                                    item.action_type === key
                                      ? { ...item, sop: e.target.value }
                                      : item
                                  );
                                  setExistingSOPs(updatedSOPs);
                                } else {
                                  setNewSOPs((prev: any) => ({
                                    ...prev,
                                    [key]: {
                                      ...prev[key],
                                      sop: e.target.value,
                                    },
                                  }));
                                }
                              }}
                            />
                          )}
                        </div>
                      );
                    })}
                </div>

                {!formData.is_perpetual && (
                  <div className="edit-container">
                    <div className="edit-container">
                    <label className="mb-1 text-md">
                      Up for Renewal (in Days)<span>*</span>
                    </label>
                    <input
                      className="form-control"
                      type="number"
                      min={0}
                      max={
                        formData.default_validity_days === ""
                          ? undefined
                          : Number(formData.default_validity_days) - 1
                      }
                      placeholder="Enter number of days before expiry"
                      value={formData.renewal_days}
                      onChange={(e) => {
                        const raw = e.target.value;
                        let value: number | string = raw === "" ? "" : Number(raw);

                        if (
                          typeof value === "number" &&
                          formData.default_validity_days !== "" &&
                          value >= Number(formData.default_validity_days)
                        ) {
                          value = Number(formData.default_validity_days) - 1;
                        }

                        handleInputChange("renewal_days", value);
                        validateRenewalDays(
                          value === "" ? 0 : Number(value),
                          formData.default_validity_days === "" ? 0 : Number(formData.default_validity_days)
                        );
                      }}
                      onWheel={(e) => e.currentTarget.blur()}
                    />
                    {validationError && (
                      <small className="text-danger mt-1 mb-0 text-xsm">
                        {validationError}
                      </small>
                    )}
                    </div>
                    <div className="edit-container">
                      <label className="mb-1 text-md">
                        Number of Renewal Reminders<span>*</span>
                      </label>
                      <select
                        className="form-select"
                        value={reminderCount}
                        onChange={(e) =>
                          setReminderCount(Number(e.target.value))
                        }
                      >
                        <option value="">Select number of reminders</option>
                        {[3, 4, 5].map((num) => (
                          <option key={num} value={num}>
                            {num}
                          </option>
                        ))}
                      </select>
                    </div>

                    {reminderCount > 0 && (
                      <div className="ps-4 mb-3 mt-3">
                        {[...Array(reminderCount)].map((_, i) => (
                          <div key={i} className="edit-container">
                            <label className="mb-1 text-md">
                              Renewal Reminder {i + 1} – to be sent before
                              expiry date
                            </label>
                            <select
                              className="form-select"
                              value={formData.reminder_days[i] || ""}
                              onChange={(e) => {
                                const newReminderDays = [
                                  ...formData.reminder_days,
                                ];
                                newReminderDays[i] = Number(e.target.value);
                                handleInputChange(
                                  "reminder_days",
                                  newReminderDays
                                );
                              }}
                            >
                              <option value="" disabled>
                                Select number of days before the expiry date
                              </option>
                              {[...Array(1000)].map((_, index) => (
                                <option key={index + 1} value={index + 1}>
                                  {index + 1}
                                </option>
                              ))}
                            </select>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            <hr />
            <div className="d-flex gap-3 w-100">
              <button
                type="button"
                className="button-secondary w-100 fw-semibold"
                onClick={onClose}
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                type="button"
                className="button-primary w-100 fw-semibold"
                onClick={handleSubmit}
                disabled={isLoading || !hasChanges}
              >
                {isLoading ? "Saving..." : "Submit"}
              </button>
            </div>
          </div>
          {isLoading && <PageLoader />}
        </div>
      </div>
      , document.body)}
    </>
  );
};

export default EditDetails;
